import { forwardRef, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { BadgeCheck, Calendar, Clock } from 'lucide-react'
import { PlayLogo } from '@/components/brand/PlayLogo'
import { Avatar, Badge, Skeleton, SkeletonGroup } from '@/components/play'
import { formatEventStart } from '@/lib/explore'
import { profileAvatarUrl } from '@/lib/watch/rules'
import type { ExploreEvent } from '@/types/explore'
import './explore.css'

/**
 * One listed event on /explore, as a single link to its public page named for the event: its cover, name, host and
 * start, which the link reads as its description. Without a cover, or when it fails to load, it shows the stacked
 * play mark, never footage.
 * Happening now is the event's scheduled window, not evidence of live video, so it is a yellow badge with a clock:
 * never the live green, a dot or the word live.
 */
export const ExploreEventCard = forwardRef<HTMLAnchorElement, { event: ExploreEvent }>(function ExploreEventCard({ event }, ref) {
  const id = useId()
  const [nameId, nowId, hostId, startId] = ['name', 'now', 'host', 'start'].map((part) => `${id}-${part}`)
  const { host } = event
  const [brokenCover, setBrokenCover] = useState<string | null>(null)
  const cover = event.cover_url && event.cover_url !== brokenCover ? event.cover_url : null
  return (
    <Link
      ref={ref}
      to={event.path}
      className="explore-card"
      aria-labelledby={nameId}
      aria-describedby={[event.happening_now && nowId, hostId, startId].filter(Boolean).join(' ')}
    >
      <span className="explore-card__cover">
        {cover
          ? <img className="explore-card__image" src={cover} alt="" loading="lazy" onError={() => setBrokenCover(cover)} />
          : <span className="explore-card__fallback" aria-hidden="true"><PlayLogo markOnly /></span>}
        {event.happening_now && <Badge id={nowId} tone="accent" icon={<Clock />} className="explore-card__now">Happening now</Badge>}
      </span>
      <h2 id={nameId} className="explore-card__name">{event.name}</h2>
      <span id={hostId} className="explore-card__host">
        <Avatar name={host.name} src={profileAvatarUrl(host.avatar_url)} size="compact" />
        <span className="sr-only">Hosted by </span>
        <span className="explore-card__host-name">{host.name}</span>
        {/* The title is the pointer's hint: a focusable tooltip can't sit inside the card's link. */}
        {host.verified && <BadgeCheck className="explore-card__verified" role="img" aria-label="Verified"><title>Verified</title></BadgeCheck>}
      </span>
      <span id={startId} className="explore-card__start">
        <Calendar aria-hidden="true" />
        <time dateTime={event.starts_at}>{formatEventStart(event.starts_at)}</time>
      </span>
    </Link>
  )
})

/** Cards in the shape of event cards while the first page loads, announced once. */
export function ExploreEventSkeletons({ count = 4 }: { count?: number }) {
  return (
    <SkeletonGroup label="Loading upcoming events">
      <div className="explore-grid">
        {Array.from({ length: count }, (_, index) => (
          <div key={index} className="explore-skeleton">
            <Skeleton shape="media" />
            <Skeleton className="explore-skeleton__name" />
            <div className="explore-skeleton__host">
              <Skeleton shape="circle" className="explore-skeleton__avatar" />
              <Skeleton className="explore-skeleton__line" />
            </div>
            <Skeleton className="explore-skeleton__line" />
          </div>
        ))}
      </div>
    </SkeletonGroup>
  )
}
